import { RequestResponse } from '@/app/common/types_and_interfaces'
import { parseRequestError } from '@/app/common/utilities'
import { CustomQueryOptions } from '@/common/types_and_interfaces.ts'
import axiosInstance from '@/libs/axios_config'
import { GETMe, GETUser, User } from '@/queries/users/requests.ts'
import { GETApplicantPermissions } from '@/apis/users/requests.ts'
import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

export const UserQueryKeys = {
  authUser: (applicantId: number) => ['authUser', applicantId],
  allUsers: ['users'],
  filteredUsers: (filter: number) => [...UserQueryKeys.allUsers, filter],
  getUser: (userId?: number) => [...UserQueryKeys.allUsers, userId],
  applicantPermissions: (programId: number) => [
    'applicantPermissions',
    programId,
  ],
} as const

export class UserQueries {
  static getUserDetail(): import('@tanstack/react-query').DefinedInitialDataOptions<
    unknown,
    Error,
    unknown,
    import('@tanstack/query-core').QueryKey
  > {
    throw new Error('Method not implemented.')
  }
  public static getAuthenticatedUser(
    id: number,
    options?: CustomQueryOptions<User>
  ) {
    return queryOptions({
      queryKey: UserQueryKeys.authUser(id),
      queryFn: () => GETMe(),
      ...options,
    })
  }

  public static getUser(id: number) {
    return queryOptions({
      queryKey: UserQueryKeys.getUser(id),
      queryFn: () => GETUser(id),
    })
  }

  public static getApplicantPermissions(programId: number) {
    return queryOptions({
      queryKey: UserQueryKeys.applicantPermissions(programId),
      queryFn: () => GETApplicantPermissions(programId),
      enabled: programId !== 0,
    })
  }
}

const UserDetailsSchema = z.object({
  id: z.number(),
  status: z.number().optional(),
  global_country_id: z.number().nullable(),
  global_state_id: z.number().nullable(),
  language_preference_id: z.number().nullable(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  city: z.string().nullable(),
  shipping_address: z.string().nullable(),
  profile_picture: z.string().nullable(),
  global_timezone_id: z.number().nullable(),
  callBackUrl: z.string().optional(),
  contact_preference: z.number().nullable(),
  user_has_accepted_text_notifications: z.number().nullable(),
})

export type UserDetails = z.infer<typeof UserDetailsSchema>

export const getUserDetail = async (userId?: string): Promise<UserDetails> => {
  try {
    const response = await axiosInstance.get(`/users/${userId}`)
    return UserDetailsSchema.parse(response.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

// update User
export type UpdateUserReq = {
  profile_picture_image?: File | string | null
  profile_picture?: File | string | null
  status?: number | null
  global_country_id?: number | null
  language_preference_id?: number | null
  global_state_id?: number | null
  first_name?: string
  last_name?: string
  city?: string
  shipping_address?: string | null
  global_timezone_id?: number | null
  phone?: string
  password?: string
  contact_preference?: number
  access_2fa?: number
  user_has_accepted_text_notifications?: number
  unread_messages?: number
  email?: string | null
}
const UpdateUserRespSchema = z.object({
  id: z.number(),
})

export type UpageUserResp = z.infer<typeof UpdateUserRespSchema>

export const UpdateUserData = async (
  requestBody: UpdateUserReq,
  id: number
): Promise<RequestResponse<UpageUserResp>> => {
  try {
    if (requestBody.profile_picture_image instanceof File) {
      requestBody.profile_picture_image = await convertFileToBase64(
        requestBody.profile_picture_image
      )
    }
    const response = await axiosInstance.put(
      `/scholar/applicants/${id}`,
      requestBody
    )
    const cleanResponse = UpdateUserRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}
